import { useCallback, useMemo } from 'react'
import { Dropdown, Row, Col, Popover } from 'antd'
import { MoreOutlined, CaretDownOutlined, CaretUpOutlined, CheckOutlined } from '@ant-design/icons'
import {
  saveNegateColumns,
  removeNegateColumn,
  saveFreeWidthColumn,
  saveAllFreeWidthColumn,
  resetFreeWidthColumns,
  saveSorting,
  setColumnToAlignLeft,
  setColumnToAlignRight,
  setColumnToUnAlign,
  showEditModal,
  setActiveDropdown,
  setActivePriceDropdown,
  addEmptyDescription,
  saveNewLineColumns,
  setEmptyColumnFromMenu,
  updateEmptyColumn,
  resetAlignedColumns,
} from '../../../Slices/Kiosk/TableOptionsSlice'
import { ArticleColumn, TableSortingAscend, TableSortingDescend } from '../../../Constants/TableConfiguration'
import { AllFreeWidthColumn } from '../../../Constants/Configuration'
import { populateTableWithArticles } from '../../../Services/Kiosk/ArticleServices'
import PinIcon from '../../Svg/PinIcon'
import FilterIcon from '../../Svg/FilterIcon'
import EditIcon from '../../Svg/EditIcon'
import {
  getHtmlParentForDropdown,
  renderTranslation,
  retrieveRenderNewLineColumn,
  stopPropagation,
} from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { retrieveActiveEmptyColumns } from '../../../Helpers/FilterHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'

function AdditionalMenu(props) {
  const dispatch = useAppDispatch()
  const negateTableColumns = useAppSelector(state => state.present.tableOptions.negateTableColumns)
  const sorting = useAppSelector(state => state.present.tableOptions.sorting)
  const freeWidthColumns = useAppSelector(state => state.present.tableOptions.freeWidthColumns)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const activeDropdown = useAppSelector(state => state.present.tableOptions.activeDropdown)
  const language = useAppSelector(state => state.present.language.value)
  const fullEditSelection = useAppSelector(state => state.present.tableOptions.fullEditSelection)
  const newLineColumns = useAppSelector(state => state.present.tableOptions.newLineColumns)
  const emptyTableColumns = useAppSelector(state => state.present.tableOptions.emptyTableColumns)
  const emptyExtraDescriptions = useAppSelector(state => state.present.tableOptions.emptyExtraDescriptions)
  const defaultCols = useAppSelector(state => state.present.tableOptions.defaultColumns)

  const negated = useMemo(() => {
    return negateTableColumns.indexOf(props.label) !== -1
  }, [negateTableColumns, props.label])
  const emptyColumns = useMemo(() => {
    return retrieveActiveEmptyColumns(emptyTableColumns, emptyExtraDescriptions)
  }, [emptyExtraDescriptions, emptyTableColumns])
  const empty = useMemo(() => {
    return emptyColumns.find(element =>
      element.languageCode ? element.field + element.languageCode === props.label : element === props.label,
    )
  }, [emptyColumns, props.label])

  const dropdownVisible = useMemo(() => {
    return activeDropdown === props.label ? true : false
  }, [activeDropdown, props.label])

  const mktColumn = useMemo(() => {
    return props.mktColumn ? props.mktColumn : ''
  }, [props.mktColumn])
  const isMarketplaceColumn = useMemo(() => {
    return props.isMarketplace
  }, [props.isMarketplace])

  const alignLeft = useCallback(() => {
    dispatch(setColumnToAlignLeft(isMarketplaceColumn ? mktColumn : props.label))
  }, [dispatch, isMarketplaceColumn, mktColumn, props.label])

  const alignRight = useCallback(() => {
    dispatch(setColumnToAlignRight(isMarketplaceColumn ? mktColumn : props.label))
  }, [dispatch, isMarketplaceColumn, mktColumn, props.label])

  const unAlign = useCallback(() => {
    dispatch(setColumnToUnAlign(isMarketplaceColumn ? mktColumn : props.label))
  }, [dispatch, isMarketplaceColumn, mktColumn, props.label])

  const showModalEdit = useCallback(
    label => {
      dispatch(showEditModal(label))
    },
    [dispatch],
  )

  const hideDropdown = useCallback(() => {
    dispatch(setActiveDropdown(''))
  }, [dispatch])

  const searchArticlesProcedure = useCallback(() => {
    dispatch(populateTableWithArticles())
  }, [dispatch])

  const switchToDefaultFiltering = useCallback(() => {
    if (negated) {
      dispatch(removeNegateColumn(props.label))
      searchArticlesProcedure()
    }

    if (empty) {
      dispatch(updateEmptyColumn({ value: props.label, active: false }))
      searchArticlesProcedure()
    }

    hideDropdown()
  }, [dispatch, negated, empty, hideDropdown, props.label, searchArticlesProcedure])

  const updateNegatedColumnSearch = useCallback(() => {
    if (empty) {
      dispatch(updateEmptyColumn({ value: props.label, active: false }))
    }

    dispatch(saveNegateColumns(props.label))
    searchArticlesProcedure()
  }, [dispatch, searchArticlesProcedure, empty, props.label])

  const addToEmptyColumn = useCallback(() => {
    dispatch(setEmptyColumnFromMenu(props.label))
    searchArticlesProcedure()
  }, [dispatch, searchArticlesProcedure, props.label])

  const addToDescriptionEmpty = useCallback(() => {
    dispatch(
      addEmptyDescription({
        languageCode: props.language,
        field: props.name,
      }),
    )
    searchArticlesProcedure()
  }, [dispatch, props.language, props.name, searchArticlesProcedure])

  const updateFreeWidthColumn = useCallback(() => {
    dispatch(saveFreeWidthColumn(props.label))
  }, [dispatch, props.label])

  const updateAllFreeWidthColumns = useCallback(() => {
    dispatch(saveAllFreeWidthColumn())
  }, [dispatch])

  const resetColumnsAlignAndWidth = useCallback(() => {
    //this reset
    dispatch(resetFreeWidthColumns())
    dispatch(resetAlignedColumns())
  }, [dispatch])

  const updateNewLine = useCallback(() => {
    dispatch(saveNewLineColumns(props.label))
  }, [dispatch, props.label])

  const openDropdown = useCallback(
    e => {
      e.preventDefault()
      stopPropagation(e)
      if (props.disableDots) return

      dispatch(setActiveDropdown(props.label))
      dispatch(setActivePriceDropdown(''))
    },
    [props.disableDots, dispatch, props.label],
  )

  const setAscendSorting = useCallback(
    e => {
      dispatch(
        saveSorting({
          sortField: isMarketplaceColumn ? mktColumn : props.label,
          sortOrder: TableSortingAscend,
          pageSize: sorting.pageSize,
          current: sorting.current,
        }),
      )
      searchArticlesProcedure()
    },
    [searchArticlesProcedure, isMarketplaceColumn, mktColumn, dispatch, props.label, sorting],
  )

  const setDescendSorting = useCallback(
    e => {
      dispatch(
        saveSorting({
          sortField: isMarketplaceColumn ? mktColumn : props.label,
          sortOrder: TableSortingDescend,
          pageSize: sorting.pageSize,
          current: sorting.current,
        }),
      )
      searchArticlesProcedure()
    },
    [searchArticlesProcedure, isMarketplaceColumn, mktColumn, dispatch, props.label, sorting],
  )

  const resetSortOrder = useCallback(
    e => {
      dispatch(
        saveSorting({
          sortField: '',
          sortOrder: '',
          pageSize: sorting.pageSize,
          current: sorting.current,
        }),
      )
      searchArticlesProcedure()
    },
    [dispatch, searchArticlesProcedure, sorting],
  )

  const setDropdownWindowListener = useCallback(() => {
    if (dropdownVisible) {
      window.addEventListener('click', hideDropdown)
    } else {
      window.removeEventListener('click', hideDropdown)
    }
    return () => {
      window.removeEventListener('click', hideDropdown)
    }
  }, [dropdownVisible, hideDropdown])

  setDropdownWindowListener()

  const downSorting = useMemo(() => {
    return isMarketplaceColumn
      ? sorting.sortField === mktColumn && sorting.sortOrder === TableSortingDescend
      : sorting.sortField === props.label && sorting.sortOrder === TableSortingDescend
  }, [isMarketplaceColumn, mktColumn, props.label, sorting.sortField, sorting.sortOrder])
  const upSorting = useMemo(() => {
    return isMarketplaceColumn
      ? sorting.sortField === mktColumn && sorting.sortOrder === TableSortingAscend
      : sorting.sortField === props.label && sorting.sortOrder === TableSortingAscend
  }, [isMarketplaceColumn, mktColumn, props.label, sorting.sortField, sorting.sortOrder])

  const isPriceColumn = useMemo(() => {
    return defaultCols.filter(item => item.key && item.key === props.label && item.price).length !== 0
  }, [defaultCols, props.label])
  const isEditIconToDisable = useMemo(() => {
    return (
      props.label === ArticleColumn.ArticleCode ||
      props.label === ArticleColumn.FarfetchId ||
      props.label === ArticleColumn.Season ||
      props.label === ArticleColumn.Supplier ||
      props.label === ArticleColumn.SuperBrand ||
      props.label === ArticleColumn.Category ||
      props.label === ArticleColumn.SuperCategory ||
      props.label === ArticleColumn.SuperColor ||
      props.label === ArticleColumn.Size ||
      props.label === ArticleColumn.StorageLocation ||
      props.label === ArticleColumn.ShortDescription ||
      props.label === ArticleColumn.SpecialDescription ||
      props.label === ArticleColumn.SizeAndFit ||
      props.label === ArticleColumn.Field1 ||
      props.label === ArticleColumn.Field2 ||
      props.label === ArticleColumn.Field3 ||
      props.label === ArticleColumn.Field4 ||
      props.label === ArticleColumn.Field5 ||
      props.label === ArticleColumn.Field6 ||
      props.label === ArticleColumn.Field7 ||
      props.label === ArticleColumn.Field8 ||
      props.label === ArticleColumn.Field9 ||
      props.label === ArticleColumn.Field10 ||
      isMarketplaceColumn ||
      (isPriceColumn && props.label.includes(ArticleColumn.OriginalRetailPricelist1Key)) ||
      (isPriceColumn && props.label.includes(ArticleColumn.OriginalRetailPricelist2Key)) ||
      (isPriceColumn && props.label === ArticleColumn.WholesalePricelist1Key) ||
      (isPriceColumn && props.label === ArticleColumn.WholesalePricelist2Key) ||
      (isPriceColumn && props.label === ArticleColumn.GrossWholesalePricelist1Key) ||
      (isPriceColumn && props.label === ArticleColumn.GrossWholesalePricelist2Key)
    )
  }, [isMarketplaceColumn, isPriceColumn, props.label])
  const isArticleCodeColumn = useMemo(() => {
    return props.label === ArticleColumn.ArticleCode
  }, [props.label])
  const isBarcodeColumn = useMemo(() => {
    return props.label === ArticleColumn.Barcode
  }, [props.label])
  const isTagColumn = props.label === ArticleColumn.Tag;
  const renderNewLine = useMemo(() => {
    return retrieveRenderNewLineColumn(props.label, defaultCols)
  }, [defaultCols, props.label])

  const menu = useMemo(() => {
    return props.language
      ? {
          className: 'additional-menu',
          onClick: ({ key, keyPath, domEvent }) => {
            stopPropagation(domEvent)
          },
          onSelect: ({ key, keyPath, domEvent }) => {},
          items: [
            {
              key: 'filter-submenu',
              label: renderTranslation(language, TranslationKeys.tableDotsMenuSetFilter),
              icon: <FilterIcon className='additional-menu-icon filter-icon' />,
              className: 'bordered exclude-modal-click',
              children: [
                {
                  key: 'submenu-empty',
                  icon: <CheckOutlined className={empty ? '' : 'v-hidden'} />,
                  onClick: addToDescriptionEmpty,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuEmpty),
                  className: 'exclude-modal-click',
                },
              ],
            },
            {
              key: 'lock-column-submenu',
              label: renderTranslation(language, TranslationKeys.tableDotsLockColumn),
              icon: <PinIcon className='additional-menu-icon pin-icon' />,
              disabled: props.label === ArticleColumn.ArticleCode,
              className: 'bordered exclude-modal-click',
              children: [
                {
                  key: 'submenu-lock-left',
                  icon: <CheckOutlined className={props.fixed === 'left' ? '' : 'v-hidden'} />,
                  className: 'exclude-modal-click',
                  onClick: alignLeft,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuLockLeft),
                },
                {
                  key: 'submenu-lock-right',
                  icon: <CheckOutlined className={props.fixed === 'right' ? '' : 'v-hidden'} />,
                  className: 'exclude-modal-click',
                  onClick: alignRight,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuLockRight),
                },
                {
                  key: 'submenu-unlock',
                  icon: <CheckOutlined className={!props.fixed ? '' : 'v-hidden'} />,
                  className: 'exclude-modal-click',
                  onClick: unAlign,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuUnlocked),
                },
              ],
            },
            {
              key: 'adapt-single-column',
              icon: <CheckOutlined className={freeWidthColumns.indexOf(props.label) !== -1 ? '' : 'v-hidden'} />,
              onClick: updateFreeWidthColumn,
              label: renderTranslation(language, TranslationKeys.tableDotsMenuAdaptColumn),
            },
            {
              key: 'adapt-all-column',
              icon: <CheckOutlined className={freeWidthColumns.indexOf(AllFreeWidthColumn) !== -1 ? '' : 'v-hidden'} />,
              onClick: updateAllFreeWidthColumns,
              label: renderTranslation(language, TranslationKeys.tableDotsMenuAdaptAllColumns),
            },
            {
              key: 'reset-column',
              icon: <CheckOutlined className='v-hidden' />,
              onClick: resetColumnsAlignAndWidth,
              className: editTableDisable ? '' : 'bordered',
              label: renderTranslation(language, TranslationKeys.tableDotsMenuResetFixedColumns),
            },
            editTableDisable
              ? null
              : {
                  key: 'edit',
                  icon: <EditIcon className={'additional-menu-icon edit-icon disable'} />,
                  className: 'edit-menu-item disable',
                  label: (
                    <Popover
                      content={renderTranslation(language, TranslationKeys.tableDotsMenuEditDisableTips)}
                      placement='right'
                      trigger='hover'
                    >
                      <div>{renderTranslation(language, TranslationKeys.tableDotsMenuEditSelection)}</div>
                    </Popover>
                  ),
                },
          ],
        }
      : {
          className: 'additional-menu',
          onClick: ({ key, keyPath, domEvent }) => {
            stopPropagation(domEvent)
          },
          onSelect: ({ key, keyPath, domEvent }) => {},
          items: [
            isBarcodeColumn
              ? null
              : {
                  key: 'order',
                  icon: <CheckOutlined className='v-hidden' />,
                  className: 'bordered',
                  label: (
                    <span>
                      {' '}
                      {renderTranslation(language, TranslationKeys.tableDotsMenuSort)}
                      {upSorting ? (
                        <CaretUpOutlined className='custom-sort-up active float-right' onClick={resetSortOrder} />
                      ) : (
                        <CaretUpOutlined className={'custom-sort-up float-right'} onClick={setAscendSorting} />
                      )}
                      {downSorting ? (
                        <CaretDownOutlined className='custom-sort-down active float-right' onClick={resetSortOrder} />
                      ) : (
                        <CaretDownOutlined className='custom-sort-down float-right' onClick={setDescendSorting} />
                      )}
                    </span>
                  ),
                },
            isMarketplaceColumn
              ? null
              : {
                  key: 'filter-submenu',
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuSetFilter),
                  icon: <FilterIcon className='additional-menu-icon filter-icon' />,
                  disabled: isPriceColumn,
                  className: 'bordered exclude-modal-click',
                  children: [
                    {
                      key: 'submenu-default',
                      icon: <CheckOutlined className={!negated && !empty ? '' : 'v-hidden'} />,
                      onClick: switchToDefaultFiltering,
                      label: renderTranslation(language, TranslationKeys.tableDotsMenuDefault),
                      className: 'exclude-modal-click',
                    },
                    isPriceColumn || isBarcodeColumn || isTagColumn
                      ? null
                      : {
                          key: 'submenu-exclusion',
                          icon: <CheckOutlined className={negated ? '' : 'v-hidden'} />,
                          onClick: updateNegatedColumnSearch,
                          label: renderTranslation(language, TranslationKeys.tableDotsMenuExceptFor),
                          className: 'exclude-modal-click',
                        },
                    isArticleCodeColumn || isPriceColumn || isTagColumn
                      ? null
                      : {
                          key: 'submenu-empty',
                          icon: <CheckOutlined className={empty ? '' : 'v-hidden'} />,
                          onClick: addToEmptyColumn,
                          label: renderTranslation(language, TranslationKeys.tableDotsMenuEmpty),
                          className: 'exclude-modal-click',
                        },
                  ],
                },
            {
              key: 'lock-column-submenu',
              label: renderTranslation(language, TranslationKeys.tableDotsLockColumn),
              icon: <PinIcon className='additional-menu-icon pin-icon' />,
              disabled: props.label === ArticleColumn.ArticleCode,
              className:
                'exclude-modal-click' + (isBarcodeColumn && editTableDisable) ||
                (isMarketplaceColumn && editTableDisable)
                  ? ''
                  : ' bordered',
              children: [
                {
                  key: 'submenu-lock-left',
                  icon: <CheckOutlined className={props.fixed === 'left' ? '' : 'v-hidden'} />,
                  className: 'exclude-modal-click',
                  onClick: alignLeft,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuLockLeft),
                },
                {
                  key: 'submenu-lock-right',
                  icon: <CheckOutlined className={props.fixed === 'right' ? '' : 'v-hidden'} />,
                  className: 'exclude-modal-click',
                  onClick: alignRight,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuLockRight),
                },
                {
                  key: 'submenu-unlock',
                  icon: <CheckOutlined className={!props.fixed ? '' : 'v-hidden'} />,
                  className: 'exclude-modal-click',
                  onClick: unAlign,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuUnlocked),
                },
              ],
            },
            isBarcodeColumn || isMarketplaceColumn
              ? null
              : {
                  key: 'adapt-single-column',
                  icon: <CheckOutlined className={freeWidthColumns.indexOf(props.label) !== -1 ? '' : 'v-hidden'} />,
                  onClick: updateFreeWidthColumn,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuAdaptColumn),
                },
            isBarcodeColumn || isMarketplaceColumn
              ? null
              : {
                  key: 'adapt-all-column',
                  icon: (
                    <CheckOutlined className={freeWidthColumns.indexOf(AllFreeWidthColumn) !== -1 ? '' : 'v-hidden'} />
                  ),
                  onClick: updateAllFreeWidthColumns,
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuAdaptAllColumns),
                },
            isBarcodeColumn || isMarketplaceColumn
              ? null
              : {
                  key: 'reset-column',
                  icon: <CheckOutlined className='v-hidden' />,
                  onClick: resetColumnsAlignAndWidth,
                  className: editTableDisable ? '' : 'bordered',
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuResetFixedColumns),
                },
            renderNewLine
              ? {
                  key: 'new-line',
                  icon: <CheckOutlined className={newLineColumns.indexOf(props.label) !== -1 ? '' : 'v-hidden'} />,
                  onClick: updateNewLine,
                  className: editTableDisable ? '' : 'bordered',
                  label: renderTranslation(language, TranslationKeys.tableDotsNewLine),
                }
              : null,
            editTableDisable
              ? null
              : isEditIconToDisable
              ? {
                  key: 'edit-disable',
                  icon: <EditIcon className={'additional-menu-icon edit-icon disable'} />,
                  className: 'edit-menu-item disable',
                  label: (
                    <Popover
                      content={renderTranslation(language, TranslationKeys.tableDotsMenuEditDisableTips)}
                      placement='right'
                      trigger='hover'
                    >
                      <div>{renderTranslation(language, TranslationKeys.tableDotsMenuEditSelection)}</div>
                    </Popover>
                  ),
                }
              : isBarcodeColumn
              ? null
              : rowKeysSelected.length > 0 || fullEditSelection
              ? {
                  key: 'edit',
                  icon: <EditIcon className={'additional-menu-icon edit-icon'} />,
                  disabled: false,
                  onClick: () => {
                    showModalEdit(props.label)
                  },
                  label: renderTranslation(language, TranslationKeys.tableDotsMenuEditSelection),
                }
              : {
                  key: 'edit-lock',
                  icon: <EditIcon className={'additional-menu-icon edit-icon disable'} />,
                  className: 'edit-menu-item locked',
                  label: (
                    <Popover
                      content={renderTranslation(language, TranslationKeys.tableDotsMenuEditLockedTips)}
                      placement='right'
                      trigger='hover'
                    >
                      <div>{renderTranslation(language, TranslationKeys.tableDotsMenuEditSelection)}</div>
                    </Popover>
                  ),
                },
          ],
        }
  }, [
    addToDescriptionEmpty,
    addToEmptyColumn,
    alignLeft,
    alignRight,
    downSorting,
    editTableDisable,
    empty,
    freeWidthColumns,
    fullEditSelection,
    isArticleCodeColumn,
    isBarcodeColumn,
    isEditIconToDisable,
    isMarketplaceColumn,
    isPriceColumn,
    language,
    negated,
    newLineColumns,
    props.fixed,
    props.label,
    props.language,
    renderNewLine,
    resetColumnsAlignAndWidth,
    resetSortOrder,
    rowKeysSelected.length,
    setAscendSorting,
    setDescendSorting,
    showModalEdit,
    switchToDefaultFiltering,
    unAlign,
    upSorting,
    updateAllFreeWidthColumns,
    updateFreeWidthColumn,
    updateNegatedColumnSearch,
    updateNewLine,
  ])

  return (
    <Dropdown
      className='additional-menu-dropdown'
      menu={menu}
      open={dropdownVisible}
      getPopupContainer={getHtmlParentForDropdown}
    >
      <Row className='adjust-table-head'>
        <Col flex='auto' className='additional-menu-content'>
          {props.children}
        </Col>
        <Col flex='10px'></Col>
        {!props.disableDots ? (
          <MoreOutlined
            className={'dots-' + props.label + ' dots-icon article-menu-dots'}
            style={{ top: 'calc(50% - 5px)', transform: 'translateY(-25%)', right: 2, position: 'absolute' }}
            onClick={openDropdown}
          />
        ) : null}
      </Row>
    </Dropdown>
  )
}

export default AdditionalMenu
